import React, { useState, useEffect } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Mail, Send, FileText, Plus, Edit2, Trash2, RefreshCw,
  Save, X, ChevronDown, ChevronUp
} from "lucide-react";

export default function PrivacyEmailManager() {
  const [tab, setTab] = useState("send"); // send | templates | history
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Send email state
  const [emailForm, setEmailForm] = useState({
    to: "",
    subject: "",
    html_content: "",
    template_id: "",
    template_variables: {},
    from_name: "GioPilos Privacy",
  });

  // Template editor state
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    template_id: "",
    name: "",
    subject: "",
    html_content: "",
    variables: [],
  });

  useEffect(() => {
    if (tab === "templates") loadTemplates();
    if (tab === "history") loadHistory();
  }, [tab]);

  const loadTemplates = async () => {
    try {
      const res = await api.get("/api/privacy-email/templates");
      setTemplates(res.data || []);
    } catch (e) {
      toast.error("Failed to load templates");
    }
  };

  const loadHistory = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/privacy-email/history?limit=50");
      setHistory(res.data.emails || []);
    } catch (e) {
      toast.error("Failed to load email history");
    } finally {
      setLoading(false);
    }
  };

  const sendEmail = async () => {
    if (!emailForm.to || !emailForm.subject) {
      toast.error("Please fill in recipient and subject");
      return;
    }

    setLoading(true);
    try {
      await api.post("/api/privacy-email/send", emailForm);
      toast.success("Email sent successfully!");
      setEmailForm({
        to: "",
        subject: "",
        html_content: "",
        template_id: "",
        template_variables: {},
        from_name: "GioPilos Privacy",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send email");
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async () => {
    if (!templateForm.template_id || !templateForm.name) {
      toast.error("Template ID and name are required");
      return;
    }

    try {
      if (editingTemplate) {
        await api.put(`/api/privacy-email/templates/${templateForm.template_id}`, templateForm);
        toast.success("Template updated!");
      } else {
        await api.post("/api/privacy-email/templates", templateForm);
        toast.success("Template created!");
      }
      setEditingTemplate(null);
      setTemplateForm({
        template_id: "",
        name: "",
        subject: "",
        html_content: "",
        variables: [],
      });
      loadTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save template");
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!confirm("Delete this template?")) return;

    try {
      await api.delete(`/api/privacy-email/templates/${templateId}`);
      toast.success("Template deleted");
      loadTemplates();
    } catch (e) {
      toast.error("Failed to delete template");
    }
  };

  const seedDefaultTemplates = async () => {
    try {
      const res = await api.post("/api/privacy-email/seed-templates");
      toast.success(res.data.message);
      loadTemplates();
    } catch (e) {
      toast.error("Failed to seed templates");
    }
  };

  return (
    <AppLayout
      title="Privacy Email Manager"
      overline="privacy@giopilos.com · EMAIL MANAGEMENT"
      actions={
        <div className="flex gap-2">
          {tab === "templates" && (
            <button
              onClick={seedDefaultTemplates}
              className="btn-ghost inline-flex items-center gap-2 text-xs">
              <RefreshCw size={14} />
              Seed Defaults
            </button>
          )}
          {tab === "history" && (
            <button
              onClick={loadHistory}
              disabled={loading}
              className="btn-ghost inline-flex items-center gap-2 text-xs">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          )}
        </div>
      }>
      
      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b pb-2" style={{ borderColor: "var(--gio-border)" }}>
        {[
          { key: "send", label: "Send Email", icon: Send },
          { key: "templates", label: "Templates", icon: FileText },
          { key: "history", label: "History", icon: Mail },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-lg inline-flex items-center gap-2 text-sm font-bold transition ${
              tab === t.key ? "" : "opacity-60 hover:opacity-100"
            }`}
            style={{
              background: tab === t.key ? "var(--gio-surface)" : "transparent",
              color: tab === t.key ? "var(--gio-text)" : "var(--gio-muted)",
              border: tab === t.key ? "1px solid var(--gio-border)" : "1px solid transparent",
            }}>
            <t.icon size={16} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Send Email Tab */}
      {tab === "send" && (
        <div className="max-w-3xl space-y-4">
          <div>
            <label className="block text-sm font-bold mb-2" style={{ color: "var(--gio-text)" }}>
              To
            </label>
            <input
              type="email"
              value={emailForm.to}
              onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })}
              placeholder="recipient@example.com"
              className="gio-input w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-bold mb-2" style={{ color: "var(--gio-text)" }}>
              Subject
            </label>
            <input
              type="text"
              value={emailForm.subject}
              onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
              placeholder="Email subject"
              className="gio-input w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-bold mb-2" style={{ color: "var(--gio-text)" }}>
              Use Template (Optional)
            </label>
            <select
              value={emailForm.template_id}
              onChange={(e) => setEmailForm({ ...emailForm, template_id: e.target.value })}
              className="gio-input w-full">
              <option value="">No template</option>
              {templates.map((t) => (
                <option key={t.template_id} value={t.template_id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-bold mb-2" style={{ color: "var(--gio-text)" }}>
              HTML Content
            </label>
            <textarea
              value={emailForm.html_content}
              onChange={(e) => setEmailForm({ ...emailForm, html_content: e.target.value })}
              placeholder="<p>Email content in HTML...</p>"
              rows={12}
              className="gio-input w-full font-mono text-xs"
            />
          </div>

          <button
            onClick={sendEmail}
            disabled={loading}
            className="btn-primary inline-flex items-center gap-2">
            <Send size={16} />
            {loading ? "Sending..." : "Send Email"}
          </button>
        </div>
      )}

      {/* Templates Tab */}
      {tab === "templates" && (
        <div className="space-y-4">
          {/* Template Editor */}
          {(editingTemplate || !editingTemplate) && (
            <div className="border rounded-lg p-5 space-y-4" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold" style={{ color: "var(--gio-text)" }}>
                  {editingTemplate ? "Edit Template" : "Create New Template"}
                </h3>
                {editingTemplate && (
                  <button
                    onClick={() => {
                      setEditingTemplate(null);
                      setTemplateForm({
                        template_id: "",
                        name: "",
                        subject: "",
                        html_content: "",
                        variables: [],
                      });
                    }}
                    className="text-xs opacity-60 hover:opacity-100">
                    <X size={18} />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold mb-2" style={{ color: "var(--gio-text)" }}>
                    Template ID
                  </label>
                  <input
                    type="text"
                    value={templateForm.template_id}
                    onChange={(e) => setTemplateForm({ ...templateForm, template_id: e.target.value })}
                    placeholder="privacy-response"
                    disabled={editingTemplate}
                    className="gio-input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-2" style={{ color: "var(--gio-text)" }}>
                    Template Name
                  </label>
                  <input
                    type="text"
                    value={templateForm.name}
                    onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                    placeholder="Privacy Response"
                    className="gio-input w-full"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold mb-2" style={{ color: "var(--gio-text)" }}>
                  Subject
                </label>
                <input
                  type="text"
                  value={templateForm.subject}
                  onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                  placeholder="Re: {{{'{{subject}}'}}}"
                  className="gio-input w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-bold mb-2" style={{ color: "var(--gio-text)" }}>
                  HTML Content
                </label>
                <textarea
                  value={templateForm.html_content}
                  onChange={(e) => setTemplateForm({ ...templateForm, html_content: e.target.value })}
                  placeholder="<div>Use {{{'{{variable}}'}} for placeholders</div>"
                  rows={10}
                  className="gio-input w-full font-mono text-xs"
                />
              </div>

              <button
                onClick={saveTemplate}
                className="btn-primary inline-flex items-center gap-2">
                <Save size={16} />
                {editingTemplate ? "Update Template" : "Create Template"}
              </button>
            </div>
          )}

          {/* Templates List */}
          <div className="space-y-2">
            <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: "var(--gio-muted)" }}>
              Saved Templates ({templates.length})
            </h3>
            {templates.map((t) => (
              <div
                key={t.template_id}
                className="border rounded-lg p-4 flex items-start justify-between"
                style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
                <div className="flex-1">
                  <div className="font-bold" style={{ color: "var(--gio-text)" }}>{t.name}</div>
                  <div className="text-xs mt-1" style={{ color: "var(--gio-muted-2)" }}>
                    ID: {t.template_id} · Subject: {t.subject}
                  </div>
                  {t.variables && t.variables.length > 0 && (
                    <div className="text-xs mt-2 flex gap-1 flex-wrap">
                      {t.variables.map((v) => (
                        <span
                          key={v}
                          className="px-2 py-0.5 rounded text-xs font-mono"
                          style={{ background: "var(--gio-primary-soft)", color: "var(--gio-primary)" }}>
                          {`{{${v}}}`}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setEditingTemplate(t.template_id);
                      setTemplateForm(t);
                    }}
                    className="p-2 rounded hover:bg-[color:var(--gio-surface-hi)]">
                    <Edit2 size={14} style={{ color: "var(--gio-muted-2)" }} />
                  </button>
                  <button
                    onClick={() => deleteTemplate(t.template_id)}
                    className="p-2 rounded hover:bg-red-500/20">
                    <Trash2 size={14} style={{ color: "#ef4444" }} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* History Tab */}
      {tab === "history" && (
        <div className="space-y-2">
          {loading && <p className="text-center py-8" style={{ color: "var(--gio-muted-2)" }}>Loading history...</p>}
          {!loading && history.length === 0 && (
            <p className="text-center py-12" style={{ color: "var(--gio-muted-3)" }}>No emails sent yet</p>
          )}
          {history.map((email) => (
            <div
              key={email.email_id || email.sent_at}
              className="border rounded-lg p-4"
              style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-bold" style={{ color: "var(--gio-text)" }}>
                      {email.subject}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-bold ${
                        email.status === "sent" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"
                      }`}>
                      {email.status}
                    </span>
                  </div>
                  <div className="text-xs mt-1" style={{ color: "var(--gio-muted-2)" }}>
                    To: {email.to} · From: {email.from_email}
                  </div>
                  <div className="text-xs mt-1" style={{ color: "var(--gio-muted-3)" }}>
                    {new Date(email.sent_at).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </AppLayout>
  );
}
